import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card.jsx';
import { Users, AlertTriangle, CheckCircle, Clock, ChevronRight, FileText, QrCode, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { generateEmployeeTicketsPdf } from '../utils/ticketsPdf';
import TicketUploadModal from './TicketUploadModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HRDashboardCompact = ({ embedded = false, onUploadCertificate, canUpload = false }) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ employees: [], summary: {} });
  const [pdfBusy, setPdfBusy] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const handleGenPdf = async (e, employee) => {
    e.stopPropagation();
    if (pdfBusy) return;
    setPdfBusy(employee.employee_email);
    const tId = toast.loading(`Generating ${employee.employee_name}'s Safety Tickets PDF…`);
    try {
      const res = await generateEmployeeTicketsPdf(
        { employee_name: employee.employee_name },
        (m) => toast.loading(m, { id: tId })
      );
      toast.success(
        `${employee.employee_name}: ${res.included} ticket(s) in the PDF` +
        `${res.excluded_expired ? `, ${res.excluded_expired} expired skipped` : ''}. Saved to their Drive folder.`,
        { id: tId, duration: 9000 }
      );
      if (res.download_url) window.open(res.download_url, '_blank');
    } catch (err) {
      toast.error(err.message || 'Failed to generate the Safety Tickets PDF', { id: tId });
    } finally {
      setPdfBusy(null);
    }
  };
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrBusyEmail, setQrBusyEmail] = useState(null);

  const handleSingleQr = async (e, employee) => {
    e.stopPropagation();
    setQrBusyEmail(employee.employee_email);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/qr-stickers`,
        { base_url: window.location.origin, emails: [employee.employee_email] },
        { responseType: 'blob', headers: { 'Authorization': `Bearer ${token}` } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR Sticker - ${employee.employee_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`QR sticker for ${employee.employee_name} downloaded — print and stick it on the hardhat.`);
    } catch (err) {
      toast.error(err.response?.status === 404
        ? 'No user account found for this employee — they need a 365Ops account first'
        : 'Could not generate the QR sticker');
    } finally {
      setQrBusyEmail(null);
    }
  };

  const handleQrStickers = async () => {
    setQrGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/qr-stickers`, { base_url: window.location.origin }, {
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Hardhat QR Stickers.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Hardhat QR sticker sheet downloaded — scanning a sticker shows that person\'s live tickets, no login needed.');
    } catch (e) {
      toast.error('Could not generate the QR stickers');
    } finally {
      setQrGenerating(false);
    }
  };

  useEffect(() => {
    fetchCertificatesOverview();
  }, []);

  const fetchCertificatesOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('Fetching certificates from:', `${API}/hr/certificates-overview`);
      const response = await axios.get(`${API}/hr/certificates-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Certificates response:', response.data);
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching certificates overview:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEmployees = () => {
    let employees = overview.employees || [];
    
    // Apply status filter
    if (filter === 'valid') {
      employees = employees.filter(e => e.expired === 0 && e.total_certificates > 0);
    } else if (filter === 'expired') {
      employees = employees.filter(e => e.expired > 0);
    } else if (filter === 'expiring') {
      employees = employees.filter(e => (e.expiring_30 + e.expiring_60 + e.expiring_90) > 0);
    } else if (filter === 'no_certs') {
      employees = employees.filter(e => e.total_certificates === 0);
    }
    
    // Apply search filter (null-safe: some records may lack name/email)
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      employees = employees.filter(e =>
        (e.employee_name || e.name || '').toLowerCase().includes(q) ||
        (e.employee_email || e.email || '').toLowerCase().includes(q)
      );
    }
    
    return employees;
  };

  const getStatusColor = (employee) => {
    if (employee.expired > 0) return 'bg-red-50 border-red-200 hover:bg-red-100';
    if (employee.expiring_30 > 0) return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
    if (employee.expiring_60 + employee.expiring_90 > 0) return 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
    return 'bg-white border-gray-200 hover:bg-gray-50';
  };

  if (loading) {
    return (
      <div className={embedded ? 'animate-fade-in' : 'animate-fade-in p-8'}>
        {!embedded && <h1 className="text-3xl font-bold text-slate-900 mb-8 drop-shadow-lg">Employee Certifications</h1>}
        <div className="text-slate-900">Loading...</div>
      </div>
    );
  }

  const filteredEmployees = getFilteredEmployees();

  return (
    <div className={embedded ? 'animate-fade-in' : 'animate-fade-in p-8 max-w-7xl mx-auto'}>
      {/* Header */}
      <div className={`${embedded ? 'mb-6' : 'mb-8'} flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4`}>
        {!embedded ? (
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2 drop-shadow-lg">Employee Certifications</h1>
            <p className="text-blue-800 font-medium drop-shadow">Safety Certificate Compliance Tracking</p>
          </div>
        ) : <div />}
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button
            data-testid="certs-qr-stickers-btn"
            disabled={qrGenerating}
            onClick={handleQrStickers}
            className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-100 disabled:opacity-70 text-slate-900 rounded-lg font-medium shadow-lg transition-colors"
          >
            {qrGenerating
              ? <><RefreshCw className="h-4 w-4 animate-spin" />Building stickers…</>
              : <><QrCode className="h-4 w-4" />Hardhat QR Stickers</>}
          </button>
          <p className="text-xs text-blue-800 drop-shadow max-w-xs sm:text-right">
            Printable sheet — scan a hardhat QR to see that person's live tickets, no login needed
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-xl transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Total Employees</p>
                <p className="text-3xl font-bold text-slate-900">{overview.summary.total_employees || 0}</p>
              </div>
              <Users className="h-10 w-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-xl transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Total Certificates</p>
                <p className="text-3xl font-bold text-slate-900">{overview.summary.total_certificates || 0}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-xl transition-all cursor-pointer" onClick={() => setFilter('expiring')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Expiring (90 Days)</p>
                <p className="text-3xl font-bold text-orange-600">{(overview.summary.total_expiring_30 || 0) + (overview.summary.total_expiring_60 || 0) + (overview.summary.total_expiring_90 || 0)}</p>
                <p className="text-xs text-orange-700 mt-1">Click to view list</p>
              </div>
              <Clock className="h-10 w-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:shadow-xl transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">Expired</p>
                <p className="text-3xl font-bold text-red-600">{overview.summary.total_expired || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <input
          type="text"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 rounded-lg border border-slate-200 bg-white/60 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('valid')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'valid'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-white/60 text-slate-700 hover:bg-white'
            }`}
          >
            Valid
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'all'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white/60 text-slate-700 hover:bg-white'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('expired')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'expired'
                ? 'bg-red-600 text-white shadow-lg'
                : 'bg-white/60 text-slate-700 hover:bg-white'
            }`}
          >
            Expired
          </button>
          <button
            onClick={() => setFilter('expiring')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'expiring'
                ? 'bg-orange-600 text-white shadow-lg'
                : 'bg-white/60 text-slate-700 hover:bg-white'
            }`}
          >
            Expiring
          </button>
          <button
            onClick={() => canUpload ? setUploadOpen(true) : onUploadCertificate ? onUploadCertificate() : navigate('/safety-tickets')}
            className="px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all ml-auto"
            data-testid="upload-certificate-btn"
          >
            + Upload Certificate
          </button>
        </div>
      </div>
      {uploadOpen && <TicketUploadModal onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); fetchCertificatesOverview(); }} />}

      {/* Employee List - Compact One-Line Format */}
      <Card className="bg-white/60 backdrop-blur-xl border-slate-200">
        <CardContent className="p-6">
          <div className="space-y-2">
            {filteredEmployees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No employees found matching the filter.
              </div>
            ) : (
              filteredEmployees.map((employee) => (
                <div
                  key={employee.employee_email}
                  onClick={() => navigate(`/hr/employee/${encodeURIComponent(employee.employee_email)}`)}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${getStatusColor(employee)}`}
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">{employee.employee_name}</h3>
                    <p className="text-sm text-gray-600">{employee.employee_email}</p>
                  </div>
                  
                  <div className="flex items-center gap-6 mr-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{employee.total_certificates}</p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                    
                    {employee.expired > 0 && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{employee.expired}</p>
                        <p className="text-xs text-red-600">Expired</p>
                      </div>
                    )}
                    
                    {employee.expiring_30 > 0 && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-orange-600">{employee.expiring_30}</p>
                        <p className="text-xs text-orange-600">30 Days</p>
                      </div>
                    )}
                    
                    {(employee.expiring_60 + employee.expiring_90) > 0 && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-700">{employee.expiring_60 + employee.expiring_90}</p>
                        <p className="text-xs text-amber-700">90 Days</p>
                      </div>
                    )}
                  </div>
                  
                  <button
                    data-testid={`single-qr-sticker-${employee.employee_email}`}
                    onClick={(e) => handleSingleQr(e, employee)}
                    disabled={qrBusyEmail === employee.employee_email}
                    title="Print this person's hardhat QR sticker"
                    className="mr-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <QrCode className="h-3.5 w-3.5" />
                    {qrBusyEmail === employee.employee_email ? '…' : 'QR'}
                  </button>

                  <button
                    data-testid={`gen-tickets-pdf-${employee.employee_email}`}
                    onClick={(e) => handleGenPdf(e, employee)}
                    disabled={pdfBusy === employee.employee_email}
                    title="Generate Safety Tickets PDF"
                    className="mr-3 inline-flex items-center gap-1 rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {pdfBusy === employee.employee_email ? 'Generating…' : 'Tickets PDF'}
                  </button>

                  <ChevronRight className="h-5 w-5 text-slate-500" />
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRDashboardCompact;
